import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/src/markdoc/tags.js'
import * as nodes from '/vercel/path0/src/markdoc/nodes.js'
const functions = {};
const schema = {
tags: tags ? (tags.default || tags) : {},
nodes: nodes ? (nodes.default || nodes) : {},
functions: functions ? (functions.default || functions) : {},
...(config ? (config.default || config) : {}),
};

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\nlayout: blog-article\n---\n\nIn a world where environmental consciousness is gaining momentum, visionary leaders are steering us toward sustainable practices. I'm Mitchell Davis, the founder of RecruitKit, and today, I'd like to share how we are making a significant impact on the environment by participating in Stripe Climate, an innovative carbon dioxide removal program developed by Stripe.\n\nOur commitment to allocating 1% of our revenue to this program is not just about corporate responsibility; **it's about leading the way toward a greener, more sustainable future**.\n\n## Understanding Stripe Climate\n\nLaunched by Stripe in September 2020, Stripe Climate is an initiative designed to combat climate change by investing in carbon removal projects. Unlike many climate initiatives focused solely on reducing emissions, Stripe Climate directly addresses the urgent need to remove CO2 from the atmosphere. It achieves this by funding projects such as direct air capture and carbon sequestration, which are instrumental in achieving global climate goals.\n\n## Our pledge\n\nWe understand the gravity of climate change here at RecruitKit. That's why we've taken a bold step by dedicating 1% of our revenue to support Stripe Climate. Our contribution is more than just a financial commitment; it's a testament to our unwavering dedication to sustainability and a challenge to other businesses in our industry to follow suit.\n\n## Why carbon removal is crucial\n\nWhile reducing emissions is undeniably essential, carbon dioxide removal (CDR) plays a pivotal role in our fight against climate change. We are already grappling with the consequences of past emissions, making it imperative to actively remove excess CO2 from the atmosphere. Stripe Climate's carbon removal projects are meticulously chosen for their effectiveness and environmental sustainability.\n\n## The ripple effect of our commitment\n\nOur involvement with Stripe Climate has a far-reaching impact. By leading the charge and committing to carbon removal, we hope to inspire other businesses to join the cause. The collective effort of companies across industries is vital for substantial progress towards a sustainable future.\n\n## Transparency and accountability\n\nStripe Climate places a strong emphasis on transparency and accountability. As participants, we can monitor the progress of the carbon removal projects we support. This transparency ensures that our contributions are making a genuine difference and that the funds are utilised effectively.\n\n## Fostering employee engagement\n\nTaking a stand against climate change can also boost employee engagement. When a company demonstrates its commitment to sustainability, it resonates with employees who share similar values. This fosters a more motivated and satisfied workforce, which can positively impact productivity and employee retention rates.\n\n## Conclusion\n\nAs the founder of RecruitKit, I am proud of our decision to collaborate with Stripe Climate and allocate 1% of our revenue to support carbon removal projects. This commitment is not just a step towards reducing our carbon footprint; it's a beacon of leadership in the fight against climate change.\n\nIn an era where addressing climate change is paramount, we must recognise our role in the solution and take action. Our dedication to sustainability stands as a testament that every contribution, no matter how modest, matters in the battle against climate change.\n\n**Together, we can pave the way to a greener and more sustainable future for generations to come.**\n\n{% call-to-action\n  title=\"Join us and commit 1% of your revenue towards carbon removal\"\n  buttonText=\"Stripe Climate\"\n  buttonHref=\"https://stripe.com/climate\"\n/%}";
const filepath = "/blog/climate-change.md";
const ast = Markdoc.parse(source);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    partials[key] = Markdoc.parse(partials[key]);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
